import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Search, X, Briefcase, Eye, CheckCircle2, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { getProfessions, createProfession, updateProfession, deleteProfession, toggleProfessionStatus, checkProfessionName, clearProfessionNameCheck, downloadProfessionSampleExcel, bulkUploadProfessions, checkProfessionBulkFormat, clearError } from '../../redux/masterSlice';
import ErrorPage from '../../components/ErrorPage';
import MasterLabel from '../../components/common/MasterLabel';

const Profession = () => {
    const { t } = useTranslation('professionmaster');
    const dispatch = useDispatch();
    const { professions, professionLoading, professionCreateLoading, professionUpdateLoading, professionDeleteLoading, professionToggleLoading, professionPagination, professionNameCheckLoading, professionNameCheckResult, professionBulkUploadLoading, professionDownloadLoading, error } = useSelector((state) => state.master);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [viewingProfession, setViewingProfession] = useState(null);
    const [editingProfession, setEditingProfession] = useState(null);
    const [formData, setFormData] = useState({
        professionName: ''
    });
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'

    // Bulk upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [bulkUploadResult, setBulkUploadResult] = useState(null);
    const [fileError, setFileError] = useState('');
    const [formatChecking, setFormatChecking] = useState(false);
    const [formatValid, setFormatValid] = useState(false);
    const fileInputRef = useRef(null);

    // Debounce search query
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Debounce profession name check
    const [debouncedNameCheck, setDebouncedNameCheck] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNameCheck(formData.professionName.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.professionName]);

    // Check profession name when it changes
    useEffect(() => {
        if (debouncedNameCheck && debouncedNameCheck.length > 0) {
            if (!editingProfession || (editingProfession && debouncedNameCheck !== editingProfession.professionName)) {
                dispatch(checkProfessionName(debouncedNameCheck));
            } else {
                dispatch(clearProfessionNameCheck());
            }
        } else {
            dispatch(clearProfessionNameCheck());
        }
    }, [debouncedNameCheck, editingProfession, dispatch]);

    // Fetch professions when search/page/tab changes
    useEffect(() => {
        dispatch(getProfessions({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery, activeTab]);

    const filteredProfessions = professions || [];
    const paginatedProfessions = filteredProfessions;

    // Pagination handler
    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Maximum length constants
    const MAX_SEARCH_LENGTH = 50;
    const MAX_NAME_LENGTH = 50;

    // Validate search input
    const validateSearchInput = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('professionmaster.search_exceed_limit', `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters`);
        }
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('professionmaster.search_only_spaces', 'Search term cannot contain only spaces');
        }
        return '';
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;
        setSearchQuery(finalValue);
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'professionName') {
            processedValue = value.replace(/[<>]/g, '');
            if (processedValue.length > MAX_NAME_LENGTH) {
                processedValue = processedValue.substring(0, MAX_NAME_LENGTH);
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.professionName.trim()) {
            newErrors.professionName = t('professionmaster.name_required', 'Profession name is required');
        } else if (formData.professionName.trim().length < 2) {
            newErrors.professionName = t('professionmaster.name_min_length', 'Profession name must be at least 2 characters');
        } else if (formData.professionName.trim().length > MAX_NAME_LENGTH) {
            newErrors.professionName = t('professionmaster.name_max_length', `Profession name cannot exceed ${MAX_NAME_LENGTH} characters`);
        } else if (!/^[A-Za-zÀ-ÿ\s\-']+$/.test(formData.professionName.trim())) {
            newErrors.professionName = t('professionmaster.name_invalid_chars', 'Profession name can only contain letters, spaces, hyphens, and apostrophes');
        }

        if (formData.professionName.trim() && professionNameCheckResult?.exists) {
            const errorMessage = professionNameCheckResult?.message || t('professionmaster.name_already_exists', 'This profession name already exists');
            newErrors.professionName = errorMessage;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle add new profession
    const handleAdd = () => {
        setEditingProfession(null);
        setFormData({ professionName: '' });
        setErrors({});
        dispatch(clearProfessionNameCheck());
        setShowModal(true);
    };

    // Handle edit profession
    const handleEdit = (profession) => {
        setEditingProfession(profession);
        setFormData({
            professionName: profession.professionName || ''
        });
        setErrors({});
        dispatch(clearProfessionNameCheck());
        setShowModal(true);
    };

    // Helper to refresh list with current filters
    const refreshList = (page = currentPage) => {
        dispatch(getProfessions({
            page,
            limit: itemsPerPage,
            search: debouncedSearchQuery,
            status: activeTab
        }));
    };

    // Helper to check if profession is inactive (handles both boolean and string status)
    const isInactiveProfession = (profession) => {
        return profession.status === false || profession.status === 'inactive';
    };

    // Handle delete profession
    const handleDelete = async (profession) => {
        try {
            const result = await Swal.fire({
                title: t('professionmaster.delete_profession_title'),
                text: t('professionmaster.delete_profession_text', { name: profession.professionName }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#4ABDBD',
                confirmButtonText: t('professionmaster.yes_delete'),
                cancelButtonText: t('professionmaster.cancel')
            });

            if (result.isConfirmed) {
                const id = profession.professionId;
                await dispatch(deleteProfession(id)).unwrap();
                toast.success(t('professionmaster.deleted_success', 'Profession has been deleted successfully.'));
                refreshList();
            }
        } catch (error) {
            // Clear the slice-level error so the page doesn't flip to ErrorPage
            // on the next render — this is a per-action failure, not a page load failure.
            dispatch(clearError());
            if (error?.inUse || error?.status === 409) {
                await Swal.fire({
                    title: t('professionmaster.cannot_delete_title', 'Cannot delete profession'),
                    text: error?.error || t('professionmaster.in_use_text', 'This profession is assigned to one or more doctors. Reassign them before deleting.'),
                    icon: 'info',
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('professionmaster.ok', 'OK')
                });
                return;
            }
            toast.warn(error?.error || t('professionmaster.delete_failed', 'Failed to delete profession.'));
        }
    };

    // Handle toggle status
    const handleStatusToggle = async (profession) => {
        const isCurrentlyInactive = isInactiveProfession(profession);
        const newStatusLabel = isCurrentlyInactive ? t('professionmaster.activate') : t('professionmaster.deactivate');

        try {
            const result = await Swal.fire({
                title: t('professionmaster.status_change_title'),
                text: t('professionmaster.status_change_text', { action: newStatusLabel, name: profession.professionName }),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('professionmaster.confirm'),
                cancelButtonText: t('professionmaster.cancel'),
            });

            if (!result.isConfirmed) return;

            await dispatch(toggleProfessionStatus({
                id: profession.professionId
            })).unwrap();

            toast.success(isCurrentlyInactive
                    ? t('professionmaster.activated_success')
                    : t('professionmaster.deactivated_success'));
            refreshList();
        } catch (error) {
            toast.warn(error?.error || t('professionmaster.status_update_failed', 'Failed to update profession status.'));
        }
    };

    // Handle save (create or update)
    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            if (editingProfession) {
                const id = editingProfession.professionId;
                await dispatch(updateProfession({
                    id: id,
                    professionName: formData.professionName.trim()
                })).unwrap();

                toast.success(t('professionmaster.updated_success', 'Profession has been updated successfully.'));
                refreshList();
            } else {
                await dispatch(createProfession({
                    professionName: formData.professionName.trim()
                })).unwrap();

                toast.success(t('professionmaster.created_success', 'Profession has been created successfully.'));
                setCurrentPage(1);
                refreshList(1);
            }

            setShowModal(false);
            setFormData({ professionName: '' });
            setEditingProfession(null);
            setErrors({});
        } catch (error) {
            toast.warn(error?.error || (editingProfession ? t('professionmaster.update_failed', 'Failed to update profession.') : t('professionmaster.create_failed', 'Failed to create profession.')));
        }
    };

    // Handle modal close
    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ professionName: '' });
        setEditingProfession(null);
        setErrors({});
        dispatch(clearProfessionNameCheck());
    };

    // Handle view profession
    const handleView = (profession) => {
        setViewingProfession(profession);
        setShowViewModal(true);
    };

    // Handle close view modal
    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingProfession(null);
    };

    // ==================== BULK UPLOAD HANDLERS ====================

    // Handle download sample excel
    const handleDownloadSample = async () => {
        try {
            await dispatch(downloadProfessionSampleExcel()).unwrap();
        } catch (error) {
            toast.warn(error?.error || t('professionmaster.download_sample_failed', 'Failed to download sample template.'));
        }
    };

    // Handle open bulk upload modal
    const handleOpenBulkUpload = () => {
        setSelectedFile(null);
        setBulkUploadResult(null);
        setFileError('');
        setFormatValid(false);
        setFormatChecking(false);
        setShowBulkUploadModal(true);
    };

    // Handle close bulk upload modal
    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false);
        setSelectedFile(null);
        setBulkUploadResult(null);
        setFileError('');
        setFormatValid(false);
        setFormatChecking(false);
    };

    // Handle file selection
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        setFileError('');
        setBulkUploadResult(null);
        setFormatValid(false);

        if (!file) {
            setSelectedFile(null);
            return;
        }

        // Validate file type
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            setFileError(t('professionmaster.invalid_file_type', 'Please upload a valid Excel file (.xlsx or .xls)'));
            setSelectedFile(null);
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setFileError(t('professionmaster.file_too_large', 'File size must not exceed 5MB'));
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);

        // Ask the backend to verify the file structure before the user clicks
        // Upload, so format problems show up in red below the dropzone.
        setFormatChecking(true);
        try {
            await dispatch(checkProfessionBulkFormat(file)).unwrap();
            setFormatValid(true);
        } catch (err) {
            setFormatValid(false);
            setFileError(err?.error || t('professionmaster.invalid_file_format', 'Invalid Excel format'));
        } finally {
            setFormatChecking(false);
        }
    };

    // Handle bulk upload submit
    const handleBulkUpload = async () => {
        if (!selectedFile) {
            setFileError(t('professionmaster.no_file_selected', 'Please select a file to upload'));
            return;
        }
        if (!formatValid) {
            setFileError(t('professionmaster.fix_format_first', 'Please fix the file format errors before uploading.'));
            return;
        }

        try {
            const result = await dispatch(bulkUploadProfessions(selectedFile)).unwrap();
            setBulkUploadResult(result);
            // Refresh the list after successful upload
            refreshList(1);
            setCurrentPage(1);
        } catch (error) {
            toast.warn(error?.error || t('professionmaster.bulk_upload_failed', 'Failed to upload professions.'));
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file) {
            // Simulate file input change
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            if (fileInputRef.current) {
                fileInputRef.current.files = dataTransfer.files;
            }
            handleFileSelect({ target: { files: [file] } });
        }
    };

    // Handle error state — only blank the page when the initial fetch fails
    // (no data yet). Per-action failures (delete/update/create/toggle) shouldn't
    // hijack the whole page; they're surfaced via toast/Swal in their handlers.
    const hasNoData = !professions || professions.length === 0;
    if (error && hasNoData && !professionLoading) {
        if (error?.status >= 500 && error?.status <= 599) {
            return <ErrorPage error={error} pageName={t('professionmaster.page_name', 'Profession Management')} />;
        }
        if (error?.message === 'Network Error') {
            return <ErrorPage error={error} pageName={t('professionmaster.page_name', 'Profession Management')} />;
        }
        return <ErrorPage error={error} pageName={t('professionmaster.page_name', 'Profession Management')} />;
    }

    return (
        <div className="w-full" data-testid="profession-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-4" data-testid="profession-master-header">
                    <h1 className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="profession-master-title">
                        {t('professionmaster.profession_management')}
                    </h1>
                    <div className="flex items-center gap-2">
                        {/* Download Sample Excel Button */}
                        {/* <button
                            onClick={handleDownloadSample}
                            disabled={professionDownloadLoading}
                            className="group flex items-center gap-2 px-4 py-2 border-2 border-[#4ABDBD] text-[#4ABDBD] rounded-xl hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('professionmaster.download_sample_template', 'Download Sample Template')}
                        >
                            {professionDownloadLoading ? (
                                <span className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-4 h-4 animate-spin group-hover:border-white group-hover:border-t-transparent"></span>
                            ) : (
                                <Download size={18} />
                            )}
                            <span className="hidden sm:inline">{t('professionmaster.sample_excel', 'Sample Excel')}</span>
                        </button> */}
                                                {/* Add Profession Button */}
                        <button
                            onClick={handleAdd}
                            className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                            data-testid="profession-master-add-button"
                        >
                            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" data-testid="profession-master-add-icon" />
                            {t('professionmaster.add_profession')}
                        </button>
                        {/* Bulk Upload Button */}
                        <button
                            onClick={handleOpenBulkUpload}
                            className="group flex items-center gap-2 px-4 py-2 border-2 border-[#3AA8A8] text-[#3AA8A8] rounded-xl hover:bg-[#3AA8A8] hover:text-white transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
                            title={t('professionmaster.bulk_upload', 'Bulk Upload')}
                            data-testid="profession-master-bulk-upload-button"
                        >
                            <Upload size={18} data-testid="profession-master-bulk-upload-icon" />
                            <span className="hidden sm:inline" data-testid="profession-master-bulk-upload-text">{t('professionmaster.bulk_upload', 'Bulk Upload')}</span>
                        </button>
                    </div>
                </div>

                {/* Tab Bar + Search Row */}
                <div className="mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="profession-master-tab-search-section">
                    <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2" data-testid="profession-master-tabs">
                        <button
                            onClick={() => { setActiveTab('active'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'active'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="profession-master-tab-active"
                        >
                            {activeTab === 'active' && (
                                <motion.div
                                    layoutId="activeProfessionStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="profession-master-tab-active-text">{t('professionmaster.active') || 'Active'}</span>
                        </button>

                        <button
                            onClick={() => { setActiveTab('inactive'); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === 'inactive'
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                            data-testid="profession-master-tab-inactive"
                        >
                            {activeTab === 'inactive' && (
                                <motion.div
                                    layoutId="activeProfessionStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20" data-testid="profession-master-tab-inactive-text">{t('professionmaster.inactive') || 'Inactive'}</span>
                        </button>
                    </div>
                    <div className="relative group w-full sm:w-1/3" data-testid="profession-master-search-container">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={22} data-testid="profession-master-search-icon" />
                        <input
                            type="text"
                            placeholder={t('professionmaster.search_placeholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`w-full pl-12 pr-4 py-2 bg-gray-50 border-2 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400 ${searchError
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200'
                                }`}
                            data-testid="profession-master-search-input"
                        />
                        {searchError && (
                            <p className="text-red-600 text-xs mt-1 ml-1" data-testid="profession-master-search-error">{searchError}</p>
                        )}
                    </div>
                </div>

                {/* Professions Grid */}
                {(!professionLoading && filteredProfessions.length === 0) ? (
                    <div className="text-center py-16 sm:py-20" data-testid="profession-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Briefcase size={48} className="text-gray-400" data-testid="profession-master-empty-icon" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" data-testid="profession-master-empty-title">
                            {searchQuery ? t('professionmaster.no_professions_found') : t('professionmaster.no_professions_yet')}
                        </h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" data-testid="profession-master-empty-text">
                            {searchQuery
                                ? t('professionmaster.try_adjusting_search')
                                : t('professionmaster.create_first_profession')}
                        </p>
                        {!searchQuery && activeTab === 'active' && (
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                                data-testid="profession-master-empty-create-button"
                            >
                                <Plus size={20} data-testid="profession-master-empty-create-icon" />
                                {t('professionmaster.create_first_profession_button')}
                            </button>
                        )}
                    </div>
                ) : professionLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="profession-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin" data-testid="profession-master-loading-spinner"></div>
                            <p className="text-gray-500 text-sm" data-testid="profession-master-loading-text">{t('professionmaster.loading_professions')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4" data-testid="profession-master-grid">
                            {paginatedProfessions.map((profession) => {
                                const isInactive = isInactiveProfession(profession);
                                const profId = profession.professionId || profession._id || profession.id;
                                return (
                                    <div
                                        key={profId}
                                        className={`group relative bg-white border rounded-lg p-4 overflow-hidden transition-all duration-300
                                        ${isInactive
                                                ? 'border-gray-200 opacity-75'
                                                : 'border-gray-200 hover:border-[#4ABDBD] hover:shadow-lg transform hover:-translate-y-0.5 cursor-pointer'
                                            }`}
                                        data-testid={`profession-master-card-${profId}`}
                                    >
                                        {/* Gradient Accent - only for active */}
                                        {!isInactive && (
                                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                                        )}

                                        {/* Toggle Switch - top right */}
                                        <div className="absolute top-3 right-3 z-20">
                                            <div
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${isInactive
                                                    ? 'bg-gray-300'
                                                    : 'bg-[#4ABDBD]'
                                                    } ${professionToggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                onClick={() => !professionToggleLoading && handleStatusToggle(profession)}
                                                data-testid={`profession-master-toggle-${profId}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${isInactive
                                                    ? 'translate-x-1'
                                                    : 'translate-x-[18px]'
                                                    }`}></span>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="mb-3 pr-12">
                                            <h3 className={`text-base font-bold line-clamp-1 mb-2 transition-colors
                                            ${isInactive ? 'text-gray-400' : 'text-gray-800 group-hover:text-[#4ABDBD]'}`}
                                                data-testid={`profession-master-card-name-${profId}`}>
                                                <MasterLabel type="masterProfession" id={profession.professionId} field="professionName" fallback={profession.professionName} />
                                            </h3>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
                                            <button
                                                onClick={() => handleView(profession)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('professionmaster.view_details')}
                                                data-testid={`profession-master-view-button-${profId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Eye className="h-4 w-4 relative z-10" data-testid={`profession-master-view-icon-${profId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(profession)}
                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden"
                                                title={t('professionmaster.edit')}
                                                data-testid={`profession-master-edit-button-${profId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Edit2 className="h-4 w-4 relative z-10" data-testid={`profession-master-edit-icon-${profId}`} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(profession)}
                                                disabled={professionDeleteLoading}
                                                className="group/btn text-red-500 hover:text-white p-2 rounded-lg bg-gradient-to-r from-red-50 to-red-50 hover:from-red-500 hover:to-red-600 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                                title={t('professionmaster.delete')}
                                                data-testid={`profession-master-delete-button-${profId}`}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Trash2 className="h-4 w-4 relative z-10" data-testid={`profession-master-delete-icon-${profId}`} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Pagination */}
                        {professionPagination.total > 0 && (
                            <Pagination
                                totalItems={professionPagination.total}
                                currentPage={professionPagination.page || currentPage}
                                itemsPerPage={professionPagination.limit || itemsPerPage}
                                onPageChange={handlePageChange}
                                custombutton={paginatedProfessions.length === 0 ? 'disable' : ''}
                            />
                        )}
                    </>
                )}

                {/* Create/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="profession-master-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="profession-master-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="profession-master-modal-title">
                                    {editingProfession ? t('professionmaster.edit_profession') : t('professionmaster.add_new_profession')}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="profession-master-modal-close-button"
                                >
                                    <X size={24} data-testid="profession-master-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6" data-testid="profession-master-form">
                                {/* Profession Name */}
                                <div data-testid="profession-master-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="profession-master-name-label">
                                        {t('professionmaster.profession_name_label')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="professionName"
                                            value={formData.professionName}
                                            onChange={handleInputChange}
                                            maxLength={MAX_NAME_LENGTH}
                                            className={`w-full px-4 py-2 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${errors.professionName || (professionNameCheckResult?.exists && formData.professionName.trim())
                                                ? 'border-red-300 bg-red-50'
                                                : professionNameCheckResult && !professionNameCheckResult.exists && formData.professionName.trim() && !professionNameCheckLoading
                                                    ? 'border-green-300'
                                                    : 'border-gray-300'
                                                }`}
                                            placeholder={t('professionmaster.profession_name_placeholder')}
                                            data-testid="profession-master-name-input"
                                        />
                                        {formData.professionName.trim() && !professionNameCheckLoading && professionNameCheckResult && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                {professionNameCheckResult.exists ? (
                                                    <X className="h-5 w-5 text-red-500" data-testid="profession-master-name-exists-icon" />
                                                ) : (
                                                    <CheckCircle2 className="h-5 w-5 text-green-500" data-testid="profession-master-name-available-icon" />
                                                )}
                                            </div>
                                        )}
                                        {professionNameCheckLoading && formData.professionName.trim() && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <div className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-5 h-5 animate-spin" data-testid="profession-master-name-check-loading"></div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.professionName && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="profession-master-name-error">{errors.professionName}</p>
                                    )}
                                    {!errors.professionName && professionNameCheckResult?.exists && formData.professionName.trim() && (
                                        <p className="mt-1 text-sm text-red-500" data-testid="profession-master-name-exists-error">
                                            {professionNameCheckResult?.message || t('professionmaster.name_already_exists', 'This profession name already exists')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseModal}
                                    disabled={professionCreateLoading || professionUpdateLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="profession-master-modal-cancel-button"
                                >
                                    {t('professionmaster.cancel')}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={professionCreateLoading || professionUpdateLoading}
                                    className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="profession-master-modal-save-button"
                                >
                                    {(professionCreateLoading || professionUpdateLoading) && (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="profession-master-modal-save-loading"></span>
                                    )}
                                    {professionCreateLoading || professionUpdateLoading
                                        ? (editingProfession ? t('professionmaster.updating') : t('professionmaster.creating'))
                                        : (editingProfession ? t('professionmaster.update') : t('professionmaster.create'))}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Profession Modal */}
                {showViewModal && viewingProfession && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="profession-master-view-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="profession-master-view-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800" data-testid="profession-master-view-modal-title">
                                    {t('professionmaster.profession_details')}
                                </h2>
                                <button
                                    onClick={handleCloseViewModal}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="profession-master-view-modal-close-button"
                                >
                                    <X size={24} data-testid="profession-master-view-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6">
                                {/* Profession Name */}
                                <div data-testid="profession-master-view-name-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="profession-master-view-name-label">
                                        {t('professionmaster.profession_name')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-semibold" data-testid="profession-master-view-name-value">
                                        <MasterLabel type="masterProfession" id={viewingProfession.professionId} field="professionName" fallback={viewingProfession.professionName} />
                                    </div>
                                </div>

                                {/* Status */}
                                <div data-testid="profession-master-view-status-field-container">
                                    <label className="block text-sm font-medium text-gray-700 mb-2" data-testid="profession-master-view-status-label">
                                        {t('professionmaster.status_label', 'Status')}
                                    </label>
                                    <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${!isInactiveProfession(viewingProfession)
                                            ? 'bg-green-100 text-green-700 border border-green-300'
                                            : 'bg-gray-200 text-gray-500 border border-gray-300'
                                            }`}
                                            data-testid="profession-master-view-status-value">
                                            {!isInactiveProfession(viewingProfession) ? t('professionmaster.active', 'Active') : t('professionmaster.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
                                <button
                                    onClick={handleCloseViewModal}
                                    className="px-6 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md"
                                    data-testid="profession-master-view-modal-close-footer-button"
                                >
                                    {t('professionmaster.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Upload Modal */}
                {showBulkUploadModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="profession-master-bulk-upload-modal">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="profession-master-bulk-upload-modal-content">
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2" data-testid="profession-master-bulk-upload-modal-title">
                                    <Upload size={22} className="text-[#4ABDBD]" data-testid="profession-master-bulk-upload-modal-title-icon" />
                                    {t('professionmaster.bulk_upload_title', 'Bulk Upload Professions')}
                                </h2>
                                <button
                                    onClick={handleCloseBulkUpload}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-testid="profession-master-bulk-upload-modal-close-button"
                                >
                                    <X size={24} data-testid="profession-master-bulk-upload-modal-close-icon" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6">
                                {/* Instructions */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="profession-master-instructions-section">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2" data-testid="profession-master-instructions-title">
                                        <AlertCircle size={16} data-testid="profession-master-instructions-icon" />
                                        {t('professionmaster.upload_instructions_title', 'Instructions')}
                                    </h3>
                                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside" data-testid="profession-master-instructions-list">
                                        <li data-testid="profession-master-instruction-item-1">{t('professionmaster.upload_instruction_1', 'Upload an Excel file (.xlsx or .xls) with a "ProfessionName" column header')}</li>
                                        <li data-testid="profession-master-instruction-item-2">{t('professionmaster.upload_instruction_2', 'Maximum file size: 5MB')}</li>
                                        <li data-testid="profession-master-instruction-item-3">{t('professionmaster.upload_instruction_3', 'Duplicate profession names will be automatically skipped')}</li>
                                        <li data-testid="profession-master-instruction-item-4">{t('professionmaster.upload_instruction_4', 'The column header must be within the first 5 rows of the file')}</li>
                                    </ul>
                                    <button
                                        onClick={handleDownloadSample}
                                        disabled={professionDownloadLoading}
                                        className="mt-3 inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-semibold underline cursor-pointer disabled:opacity-50"
                                        data-testid="profession-master-download-sample-button"
                                    >
                                        {professionDownloadLoading ? (
                                            <span className="border-2 border-blue-700 border-t-transparent rounded-full w-3 h-3 animate-spin" data-testid="profession-master-download-sample-loading"></span>
                                        ) : (
                                            <Download size={14} data-testid="profession-master-download-sample-icon" />
                                        )}
                                        {t('professionmaster.download_sample_link', 'Download sample template')}
                                    </button>
                                </div>

                                {/* File Upload Area */}
                                {!bulkUploadResult && (
                                    <div
                                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${selectedFile
                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/5'
                                            : fileError
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300 hover:border-[#4ABDBD] hover:bg-gray-50'
                                            }`}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                        data-testid="profession-master-file-dropzone"
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            id="professionBulkUploadInput"
                                            data-testid="profession-master-file-input"
                                        />
                                        {selectedFile ? (
                                            <div className="flex flex-col items-center gap-3" data-testid="profession-master-selected-file-container">
                                                <div className="w-16 h-16 bg-[#4ABDBD]/10 rounded-full flex items-center justify-center">
                                                    <FileSpreadsheet size={32} className="text-[#4ABDBD]" data-testid="profession-master-selected-file-icon" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800" data-testid="profession-master-selected-file-name">{selectedFile.name}</p>
                                                    <p className="text-xs text-gray-500 mt-1" data-testid="profession-master-selected-file-size">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedFile(null);
                                                        setFileError('');
                                                        setFormatValid(false);
                                                        setFormatChecking(false);
                                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                                    }}
                                                    className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1 cursor-pointer"
                                                    data-testid="profession-master-remove-file-button"
                                                >
                                                    <X size={14} data-testid="profession-master-remove-file-icon" />
                                                    {t('professionmaster.remove_file', 'Remove file')}
                                                </button>
                                                {formatChecking && (
                                                    <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="profession-master-format-checking">
                                                        <span className="border-2 border-gray-400 border-t-transparent rounded-full w-3 h-3 animate-spin"></span>
                                                        {t('professionmaster.checking_format', 'Checking file format...')}
                                                    </p>
                                                )}
                                                {!formatChecking && formatValid && !fileError && (
                                                    <p className="text-xs text-green-600 flex items-center gap-1" data-testid="profession-master-format-valid">
                                                        <CheckCircle size={12} />
                                                        {t('professionmaster.format_valid', 'File format looks good')}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <label htmlFor="professionBulkUploadInput" className="cursor-pointer" data-testid="profession-master-file-dropzone-label">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                                        <Upload size={32} className="text-gray-400" data-testid="profession-master-file-dropzone-icon" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-700" data-testid="profession-master-file-dropzone-text">
                                                            {t('professionmaster.drag_drop_or_click', 'Drag & drop or click to upload')}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1" data-testid="profession-master-file-dropzone-hint">
                                                            {t('professionmaster.accepted_formats', 'Accepted formats: .xlsx, .xls (Max 5MB)')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </label>
                                        )}
                                        {fileError && (
                                            <p className="mt-3 text-sm text-red-500 flex items-center justify-center gap-1" data-testid="profession-master-file-error">
                                                <AlertCircle size={14} data-testid="profession-master-file-error-icon" />
                                                {fileError}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Upload Result */}
                                {bulkUploadResult && (
                                    <div className="space-y-4" data-testid="profession-master-upload-result-container">
                                        {/* Summary */}
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="profession-master-upload-summary-section">
                                            <h3 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2" data-testid="profession-master-upload-summary-title">
                                                <CheckCircle size={16} data-testid="profession-master-upload-summary-icon" />
                                                {t('professionmaster.upload_completed', 'Upload Completed')}
                                            </h3>
                                            <div className="grid grid-cols-3 gap-3">
                                                <div className="bg-white rounded-lg p-3 text-center border border-green-100" data-testid="profession-master-summary-created-card">
                                                    <p className="text-2xl font-bold text-green-600" data-testid="profession-master-summary-created-count">{bulkUploadResult.summary?.totalCreated || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1" data-testid="profession-master-summary-created-label">{t('professionmaster.created_count', 'Created')}</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 text-center border border-yellow-100" data-testid="profession-master-summary-duplicates-card">
                                                    <p className="text-2xl font-bold text-yellow-600" data-testid="profession-master-summary-duplicates-count">{bulkUploadResult.summary?.totalDuplicates || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1" data-testid="profession-master-summary-duplicates-label">{t('professionmaster.duplicates_count', 'Duplicates')}</p>
                                                </div>
                                                <div className="bg-white rounded-lg p-3 text-center border border-red-100" data-testid="profession-master-summary-errors-card">
                                                    <p className="text-2xl font-bold text-red-600" data-testid="profession-master-summary-errors-count">{bulkUploadResult.summary?.totalErrors || 0}</p>
                                                    <p className="text-xs text-gray-600 mt-1" data-testid="profession-master-summary-errors-label">{t('professionmaster.errors_count', 'Errors')}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Created items */}
                                        {bulkUploadResult.created && bulkUploadResult.created.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="profession-master-created-items-section">
                                                <div className="bg-green-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-green-800" data-testid="profession-master-created-items-title">
                                                        {t('professionmaster.created_items', 'Created Professions')} ({bulkUploadResult.created.length})
                                                    </p>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto">
                                                    {bulkUploadResult.created.map((item, index) => (
                                                        <div key={index} className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2" data-testid={`profession-master-created-row-${index}`}>
                                                            <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                                                            <span className="font-medium">{item.professionName}</span>
                                                            <span className="text-xs text-gray-400">({item.professionId})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Duplicate items */}
                                        {bulkUploadResult.duplicates && bulkUploadResult.duplicates.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="profession-master-duplicate-items-section">
                                                <div className="bg-yellow-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-yellow-800" data-testid="profession-master-duplicate-items-title">
                                                        {t('professionmaster.duplicate_items', 'Skipped Duplicates')} ({bulkUploadResult.duplicates.length})
                                                    </p>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto">
                                                    {bulkUploadResult.duplicates.map((item, index) => (
                                                        <div key={index} className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-0 flex items-center gap-2" data-testid={`profession-master-duplicate-row-${index}`}>
                                                            <AlertCircle size={14} className="text-yellow-500 flex-shrink-0" />
                                                            <span>{t('professionmaster.row', 'Row')} {item.row}:</span>
                                                            <span className="font-medium">{item.professionName}</span>
                                                            <span className="text-xs text-gray-400">({t('professionmaster.existing_id', 'Existing')}: {item.existingId})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Error items */}
                                        {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                                            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="profession-master-error-items-section">
                                                <div className="bg-red-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-sm font-semibold text-red-800" data-testid="profession-master-error-items-title">
                                                        {t('professionmaster.error_items', 'Errors')} ({bulkUploadResult.errors.length})
                                                    </p>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto">
                                                    {bulkUploadResult.errors.map((item, index) => (
                                                        <div key={index} className="px-4 py-2 text-sm text-red-700 border-b border-gray-100 last:border-0 flex items-center gap-2" data-testid={`profession-master-error-row-${index}`}>
                                                            <X size={14} className="text-red-500 flex-shrink-0" />
                                                            <span>{item.row ? `${t('professionmaster.row', 'Row')} ${item.row}: ` : ''}{item.message || item.error || t('professionmaster.unknown_error', 'Unknown error')}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex gap-3">
                                <button
                                    onClick={handleCloseBulkUpload}
                                    disabled={professionBulkUploadLoading}
                                    className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="profession-master-bulk-upload-modal-cancel-button"
                                >
                                    {bulkUploadResult ? t('professionmaster.close', 'Close') : t('professionmaster.cancel')}
                                </button>
                                {!bulkUploadResult && (
                                    <button
                                        onClick={handleBulkUpload}
                                        disabled={professionBulkUploadLoading || !selectedFile || formatChecking || !formatValid}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid="profession-master-bulk-upload-submit-button"
                                    >
                                        {professionBulkUploadLoading && (
                                            <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="profession-master-bulk-upload-submit-loading"></span>
                                        )}
                                        {professionBulkUploadLoading
                                            ? t('professionmaster.uploading', 'Uploading...')
                                            : t('professionmaster.upload_file', 'Upload File')}
                                    </button>
                                )}
                                {bulkUploadResult && (
                                    <button
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setBulkUploadResult(null);
                                            setFileError('');
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium shadow-md flex items-center justify-center gap-2"
                                        data-testid="profession-master-upload-another-button"
                                    >
                                        <Upload size={16} data-testid="profession-master-upload-another-icon" />
                                        <span data-testid="profession-master-upload-another-text">{t('professionmaster.upload_another', 'Upload Another File')}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profession;
